import {
  AutoGraph as ImpactIcon,
  Dashboard as OverviewIcon,
  LiveTv as LiveViewIcon,
  MonitorHeart as LiveAlertsIcon,
  Analytics as AnalyticsIcon,
  Assignment as IncidentsIcon,
  History as AuditIcon,
  Business as WorkflowIcon,
  RocketLaunch as BringUpIcon,
  Settings as SetupIcon,
  FactCheck as PreflightIcon,
  Devices as DevicesIcon,
  DashboardCustomize as DeviceControlIcon,
  Videocam as CamerasIcon,
  Map as ZonesIcon,
  Layers as SpatialOperationsIcon,
  Slideshow as ShowcaseIcon,
  Policy as PoliciesIcon,
  Backup as BlueprintsIcon,
  SmartToy as AgentsIcon,
  CloudUpload as DeploymentsIcon,
  BugReport as DiagnosticsIcon,
  BarChart as MetricsIcon,
  Insights as AccuracyIcon,
  AutoFixHigh as RunbooksIcon,
  Storage as DrIcon,
  AssignmentTurnedIn as AcceptanceIcon,
  Group as UsersIcon,
  AdminPanelSettings as AccessScopeIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  getCapabilitiesFromUser,
  hasAnyCapability,
  hasCapability,
  hasHealthNavAccess,
  hasLiveOpsNavAccess,
  type WarehouseSurfaceAccessKey,
  canOpenAcceptanceReportFlow as evalCanOpenAcceptanceReportFlow,
  canManageAcceptanceArtifacts as evalCanManageAcceptanceArtifacts,
  canManageAccessScope,
  canViewAcceptanceNav,
  isExternalSafetyReviewer,
  isBoundedEvaluationWarehouseUser,
  isProductEvaluatorCapabilities,
  isSandboxEhsEvaluatorCapabilities,
  canAccessWarehouseSurfaceForUser,
} from '../auth/access';
import { getPersonaPresetById } from '../auth/personaPresets';
import type { NavContext } from './navContext';

/** Re-export for callers that historically imported capability helpers from this module. */
export { hasCapability, hasAnyCapability, getCapabilitiesFromUser } from '../auth/access';

export type WarehouseSectionKey =
  | 'impact'
  | 'overview'
  | 'liveOps'
  | 'setupRollout'
  | 'acceptance'
  | 'access';

type UserLike = {
  role?: string | null;
  capabilities?: string[] | null;
  default_home?: string | null;
  primary_profile?: string | null;
  secondary_profiles?: string[] | null;
} | null | undefined;

export type WarehouseProductProfile = 'operations' | 'setup' | 'acceptance' | 'admin';

type WarehousePageDefinition = {
  key: string;
  text: string;
  path: string;
  section: WarehouseSectionKey;
  icon: SvgIconComponent;
  access: WarehouseSurfaceAccessKey;
  /** Lane sections: 'primary' = canonical workflow, 'advanced' = collapsed tools in sidebar */
  navGroup?: 'primary' | 'advanced';
  /** Reachable from lane only; do not show in sidebar */
  hideFromNav?: boolean;
};

export type WarehouseSectionDefinition = {
  key: WarehouseSectionKey;
  label: string;
};

export type WarehouseNavPage = WarehousePageDefinition;

export type WarehouseNavSection = {
  key: WarehouseSectionKey;
  label: string;
  /** Lane home target used by the top-level sidebar row when the section has one. */
  homePage?: WarehouseNavPage;
  pages: WarehouseNavPage[];
  /** Distinct child surfaces shown beneath the lane row. */
  primaryPages?: WarehouseNavPage[];
  /** Collapsed advanced tools (collapsed by default in sidebar) */
  advancedPages?: WarehouseNavPage[];
};

const LANE_HOME_PAGE_KEYS: Partial<Record<WarehouseSectionKey, string>> = {
  impact: 'impact',
  overview: 'overview',
  liveOps: 'incidents',
  setupRollout: 'setup-home',
  acceptance: 'reports',
};

const PROFILE_SECTION_KEYS: Record<WarehouseProductProfile, WarehouseSectionKey[]> = {
  operations: ['liveOps'],
  setup: ['setupRollout'],
  acceptance: ['acceptance'],
  admin: ['access'],
};

const PROFILE_PREFERRED_PATHS: Record<WarehouseProductProfile, string[]> = {
  operations: ['/incidents', '/live-ops', '/live-view'],
  setup: ['/setup-rollout', '/preflight', '/devices'],
  acceptance: ['/acceptance/reports', '/acceptance/overview', '/acceptance/proof'],
  admin: ['/admin/access-scope', '/users'],
};

/** EPIC-165: restricted sidebar paths for product evaluators (real warehouse shell). */
export const PRODUCT_EVALUATOR_NAV_PATHS = [
  '/live-view',
  '/incidents',
  '/cameras',
  '/zones',
  '/policies',
  '/acceptance/reports',
] as const;

/** Evaluator surfaces shown as primary nav (not buried under Advanced tools). */
const PRODUCT_EVALUATOR_PRIMARY_NAV_PATHS = new Set<string>([
  '/live-view',
  '/incidents',
  '/cameras',
  '/zones',
  '/policies',
  '/acceptance/reports',
]);

function applyProductEvaluatorNavPresentation(pages: WarehousePageDefinition[]): WarehousePageDefinition[] {
  return pages.map((page) => {
    if (!PRODUCT_EVALUATOR_PRIMARY_NAV_PATHS.has(page.path)) return page;
    const promoted = { ...page, navGroup: 'primary' as const };
    if (page.path === '/live-view') {
      return { ...promoted, text: 'Camera View' };
    }
    return promoted;
  });
}

const PRODUCT_PROFILE_ALIASES: Record<string, WarehouseProductProfile> = {
  operations: 'operations',
  operation: 'operations',
  ops: 'operations',
  live_ops: 'operations',
  'live-ops': 'operations',
  liveops: 'operations',
  product_evaluator: 'operations',
  setup: 'setup',
  rollout: 'setup',
  acceptance: 'acceptance',
  admin: 'admin',
  administration: 'admin',
  access: 'admin',
};

export const WAREHOUSE_SECTION_DEFINITIONS: WarehouseSectionDefinition[] = [
  { key: 'impact', label: 'Impact' },
  { key: 'overview', label: 'Overview' },
  { key: 'liveOps', label: 'Investigate' },
  { key: 'setupRollout', label: 'Configure' },
  { key: 'acceptance', label: 'Safety Cases' },
  { key: 'access', label: 'Admin' },
];

export function normalizeWarehouseProductProfile(value: string | null | undefined): WarehouseProductProfile | null {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return null;
  return PRODUCT_PROFILE_ALIASES[key] || null;
}

function getDeclaredWarehouseProfiles(user: UserLike): {
  primary: WarehouseProductProfile | null;
  secondary: WarehouseProductProfile[];
} {
  const primary = normalizeWarehouseProductProfile((user as any)?.primary_profile);
  const rawSecondary = Array.isArray((user as any)?.secondary_profiles)
    ? (user as any).secondary_profiles
    : [];
  const secondary = rawSecondary
    .map((value: string) => normalizeWarehouseProductProfile(value))
    .filter((value: WarehouseProductProfile | null, index: number, all: Array<WarehouseProductProfile | null>) => {
      return Boolean(value) && all.indexOf(value) === index && value !== primary;
    }) as WarehouseProductProfile[];
  return { primary, secondary };
}

function getAllowedSectionKeysForProfiles(
  primary: WarehouseProductProfile | null,
  secondary: WarehouseProductProfile[],
): Set<WarehouseSectionKey> | null {
  if (!primary) return null;
  const allowed = new Set<WarehouseSectionKey>(['overview']);
  [primary, ...secondary].forEach((profile) => {
    PROFILE_SECTION_KEYS[profile].forEach((sectionKey) => allowed.add(sectionKey));
  });
  return allowed;
}

function getProfileSectionOrder(
  primary: WarehouseProductProfile | null,
  secondary: WarehouseProductProfile[],
): WarehouseSectionKey[] | null {
  if (!primary) return null;
  const allowedSections = getAllowedSectionKeysForProfiles(primary, secondary);
  if (!allowedSections) return null;
  return WAREHOUSE_SECTION_DEFINITIONS.map((section) => section.key).filter((key) => allowedSections.has(key));
}

function getDeclaredProfileLandingPath(
  primary: WarehouseProductProfile | null,
  secondary: WarehouseProductProfile[],
  visiblePaths: Set<string>,
): string | null {
  if (!primary) return null;
  for (const profile of [primary, ...secondary]) {
    const preferred = PROFILE_PREFERRED_PATHS[profile].find((path) => visiblePaths.has(path));
    if (preferred) return preferred;
  }
  return null;
}

export type WarehouseCapabilityProfile = {
  capabilities: string[];
  hasAdminAccess: boolean;
  canReadIncidents: boolean;
  canReadAudit: boolean;
  canReadDevices: boolean;
  canReadDeployments: boolean;
  canReadCameras: boolean;
  canReadPolicies: boolean;
  canReadBlueprints: boolean;
  hasSetupReadAccess: boolean;
  hasSetupWriteAccess: boolean;
  hasLiveOpsAccess: boolean;
  hasHealthAccess: boolean;
  hasAcceptanceAccess: boolean;
  canOpenAcceptanceReportFlow: boolean;
  canManageAcceptanceArtifacts: boolean;
  canManageAccessScope: boolean;
};

export function getWarehouseCapabilityProfile(user: UserLike): WarehouseCapabilityProfile {
  const capabilities = getCapabilitiesFromUser(user);
  const hasAdminAccess = hasCapability(capabilities, 'admin:*');
  const canReadIncidents = hasCapability(capabilities, 'incidents:read');
  const canReadAudit = hasCapability(capabilities, 'audit:read');
  const canReadDevices = hasCapability(capabilities, 'devices:read');
  const canReadDeployments = hasCapability(capabilities, 'deployments:read');
  const canReadCameras = hasCapability(capabilities, 'cameras:read');
  const canReadPolicies = hasCapability(capabilities, 'policies:read');
  const canReadBlueprints = hasCapability(capabilities, 'blueprints:read');
  const hasSetupReadAccess = hasAnyCapability(capabilities, [
    'devices:read',
    'deployments:read',
    'cameras:read',
    'sites:read',
    'policies:read',
    'blueprints:read',
  ]);
  const hasSetupWriteAccess =
    hasAdminAccess ||
    hasAnyCapability(capabilities, ['devices:write', 'deployments:write', 'policies:write']);
  const hasLiveOpsAccess = hasLiveOpsNavAccess(capabilities);
  const hasHealthAccess = hasHealthNavAccess(capabilities);
  const hasAcceptanceAccess = canViewAcceptanceNav(capabilities);
  const canOpenAcceptanceReportFlow = evalCanOpenAcceptanceReportFlow(capabilities);
  const canManageAcceptanceArtifacts = evalCanManageAcceptanceArtifacts(capabilities);
  const canManageAccessScopeFlag = canManageAccessScope(capabilities);
  return {
    capabilities,
    hasAdminAccess,
    canReadIncidents,
    canReadAudit,
    canReadDevices,
    canReadDeployments,
    canReadCameras,
    canReadPolicies,
    canReadBlueprints,
    hasSetupReadAccess,
    hasSetupWriteAccess,
    hasLiveOpsAccess,
    hasHealthAccess,
    hasAcceptanceAccess,
    canOpenAcceptanceReportFlow,
    canManageAcceptanceArtifacts,
    canManageAccessScope: canManageAccessScopeFlag,
  };
}

/** Temporary Make It Safe booth shortcut. Exact working Showcase query string. */
export const SHOWCASE_BOOTH_SHORTCUT_HREF =
  '/showcase?booth_camera_id=cam_a1bc71b70d77&booth_zone_id=rae_open_new_qual&booth_policy_id=pol_zone_intrusion_cbcf5066&booth_device_id=demo5&forklift_device_id=demo5&forklift_camera_id=cam_walkway_zone_demo&forklift_condition_id=d8d39195-eba6-5b11-92f2-ad6671071df9&cta_url=https%3A%2F%2Fedgentik.com%2Fspatial-operations-view.html&qr_src=%2Fshowcase-assets%2Fcta-qr.png';

/** Temporary booth shortcut: keep Showcase visible from Overview so the working URL is one click away. */
export function isShowcaseSidebarEnabled(): boolean {
  return true;
}

const WAREHOUSE_PAGE_DEFINITIONS: WarehousePageDefinition[] = [
  { key: 'impact', text: 'Impact', path: '/acceptance/impact', section: 'impact', icon: ImpactIcon, hideFromNav: true, access: 'acceptance' },
  { key: 'overview', text: 'Overview', path: '/acceptance/overview', section: 'overview', icon: OverviewIcon, access: 'overview' },
  { key: 'spatial-operations', text: 'Spatial Operations', path: '/spatial-operations', section: 'overview', icon: SpatialOperationsIcon, navGroup: 'primary', access: 'overview' },
  { key: 'showcase', text: 'Showcase', path: SHOWCASE_BOOTH_SHORTCUT_HREF, section: 'overview', icon: ShowcaseIcon, navGroup: 'primary', access: 'overview' },
  { key: 'live-ops-home', text: 'Operations Alias', path: '/live-ops', section: 'liveOps', icon: IncidentsIcon, access: 'liveOps' },
  { key: 'incidents', text: 'Incident review', path: '/incidents', section: 'liveOps', icon: IncidentsIcon, navGroup: 'primary', access: 'incidents' },
  { key: 'live-view', text: 'Live View', path: '/live-view', section: 'liveOps', icon: LiveViewIcon, navGroup: 'advanced', access: 'liveView' },
  { key: 'live-alerts', text: 'Live Alerts', path: '/live', section: 'liveOps', icon: LiveAlertsIcon, navGroup: 'advanced', access: 'liveAlerts' },
  { key: 'analytics', text: 'Risk Analytics', path: '/analytics', section: 'liveOps', icon: AnalyticsIcon, navGroup: 'advanced', access: 'analytics' },
  { key: 'accuracy', text: 'Measure detection readiness', path: '/accuracy', section: 'setupRollout', icon: AccuracyIcon, navGroup: 'primary', access: 'accuracy' },
  { key: 'audit', text: 'Audit Log', path: '/audit', section: 'liveOps', icon: AuditIcon, navGroup: 'advanced', access: 'audit' },
  { key: 'odoo', text: 'Safety Workflows', path: '/odoo', section: 'liveOps', icon: WorkflowIcon, navGroup: 'advanced', access: 'odoo' },

  { key: 'setup-home', text: 'Configure home', path: '/setup-rollout', section: 'setupRollout', icon: BringUpIcon, navGroup: 'primary', access: 'setupRollout' },
  { key: 'bringup', text: 'Bring Up Device', path: '/bringup', section: 'setupRollout', icon: BringUpIcon, navGroup: 'advanced', hideFromNav: true, access: 'bringup' },
  { key: 'preflight', text: 'Preflight', path: '/preflight', section: 'setupRollout', icon: PreflightIcon, navGroup: 'primary', access: 'preflight' },
  { key: 'devices', text: 'Devices & Gateways', path: '/devices', section: 'setupRollout', icon: DevicesIcon, navGroup: 'advanced', access: 'devices' },
  { key: 'device-control', text: 'Device Control', path: '/devices/control', section: 'setupRollout', icon: DeviceControlIcon, navGroup: 'advanced', access: 'deviceControl' },
  { key: 'cameras', text: 'Cameras', path: '/cameras', section: 'setupRollout', icon: CamerasIcon, navGroup: 'advanced', access: 'cameras' },
  { key: 'zones', text: 'Zones', path: '/zones', section: 'setupRollout', icon: ZonesIcon, navGroup: 'advanced', access: 'zones' },
  { key: 'policies', text: 'Policies', path: '/policies', section: 'setupRollout', icon: PoliciesIcon, navGroup: 'advanced', access: 'policies' },
  { key: 'blueprints', text: 'Runtime Blueprints', path: '/blueprints', section: 'setupRollout', icon: BlueprintsIcon, navGroup: 'advanced', access: 'blueprints' },
  { key: 'config-snapshots', text: 'Control-plane Snapshots', path: '/config-snapshots', section: 'setupRollout', icon: BlueprintsIcon, navGroup: 'advanced', access: 'configSnapshots' },
  { key: 'agents', text: 'Agents', path: '/agents', section: 'setupRollout', icon: AgentsIcon, navGroup: 'advanced', access: 'agents' },
  { key: 'configurations', text: 'Missions & Settings', path: '/configurations', section: 'setupRollout', icon: SetupIcon, navGroup: 'advanced', access: 'configurations' },
  { key: 'deployments', text: 'Deployments', path: '/deployments', section: 'setupRollout', icon: DeploymentsIcon, navGroup: 'primary', access: 'deployments' },

  // Epic 54: diagnostics and recovery stay inside Setup instead of becoming Admin.
  { key: 'diagnostics', text: 'Diagnostics', path: '/diagnostics', section: 'setupRollout', icon: DiagnosticsIcon, navGroup: 'advanced', access: 'diagnostics' },
  { key: 'metrics', text: 'System Metrics', path: '/grafana', section: 'setupRollout', icon: MetricsIcon, navGroup: 'advanced', hideFromNav: true, access: 'grafana' },
  { key: 'runbooks', text: 'Ops Runbooks', path: '/ops/runbooks', section: 'setupRollout', icon: RunbooksIcon, navGroup: 'advanced', hideFromNav: true, access: 'opsRunbooks' },
  { key: 'copilot', text: 'Embedded Copilot', path: '/ops/copilot', section: 'setupRollout', icon: AgentsIcon, navGroup: 'advanced', hideFromNav: true, access: 'opsCopilot' },
  { key: 'dr', text: 'DR & Continuity', path: '/ops/dr', section: 'setupRollout', icon: DrIcon, navGroup: 'advanced', hideFromNav: true, access: 'opsDr' },

  {
    key: 'reports',
    text: 'Safety Cases',
    path: '/acceptance/reports',
    section: 'acceptance',
    icon: AcceptanceIcon,
    access: 'acceptance',
  },
  {
    key: 'cases',
    text: 'Safety Cases',
    path: '/acceptance/cases',
    section: 'acceptance',
    icon: AcceptanceIcon,
    navGroup: 'primary',
    hideFromNav: true,
    access: 'acceptance',
  },
  {
    key: 'prove',
    text: 'Safety Scan Workspace',
    path: '/acceptance/proof',
    section: 'acceptance',
    icon: AcceptanceIcon,
    navGroup: 'primary',
    hideFromNav: true,
    access: 'acceptance',
  },

  { key: 'access-scope', text: 'Access Scope', path: '/admin/access-scope', section: 'access', icon: AccessScopeIcon, access: 'accessScope' },
  { key: 'users', text: 'Users', path: '/users', section: 'access', icon: UsersIcon, access: 'users' },
];

export function resolveWarehouseNavigation(user: UserLike): WarehouseNavSection[] {
  const capabilities = getCapabilitiesFromUser(user);
  if (isExternalSafetyReviewer(user) && !hasCapability(capabilities, 'incidents:read')) {
    return [];
  }
  const boundedEvaluation = isBoundedEvaluationWarehouseUser(user);
  const { primary, secondary } = getDeclaredWarehouseProfiles(user);
  // admin:* keeps every capability-granted lane visible. A membership primary_profile
  // still focuses landing for non-admin personas, but must not hide Investigate /
  // Configure / Admin from the production Overview shell.
  const keepFullCapabilityShell = hasCapability(capabilities, 'admin:*');
  const allowedSections = boundedEvaluation || keepFullCapabilityShell
    ? null
    : getAllowedSectionKeysForProfiles(primary, secondary);
  const sectionOrder = boundedEvaluation || keepFullCapabilityShell
    ? null
    : getProfileSectionOrder(primary, secondary);
  const visiblePages = WAREHOUSE_PAGE_DEFINITIONS.filter((page) => {
    if (page.key === 'showcase' && !isShowcaseSidebarEnabled()) return false;
    if (boundedEvaluation && !PRODUCT_EVALUATOR_NAV_PATHS.includes(page.path as typeof PRODUCT_EVALUATOR_NAV_PATHS[number])) {
      return false;
    }
    return canAccessWarehouseSurfaceForUser(user, page.access);
  });
  const navPages = boundedEvaluation ? applyProductEvaluatorNavPresentation(visiblePages) : visiblePages;

  const sections = WAREHOUSE_SECTION_DEFINITIONS.map(({ key: sectionKey, label }) => {
    const sectionPages = navPages.filter((page) => page.section === sectionKey && !page.hideFromNav);
    const homePageKey = LANE_HOME_PAGE_KEYS[sectionKey];
    const primary = sectionPages.filter((p) => p.navGroup === 'primary' && p.key !== homePageKey);
    const advanced = sectionPages.filter((p) => p.navGroup === 'advanced' && !p.hideFromNav);
    const homePage =
      (homePageKey ? sectionPages.find((page) => page.key === homePageKey) : undefined) ||
      (sectionKey === 'liveOps' ? sectionPages.find((page) => page.key === 'live-ops-home') : undefined) ||
      primary[0] ||
      advanced[0];

    if (sectionKey === 'access') {
      const orderedAdminPages = ['/users', '/admin/access-scope']
        .map((path) => sectionPages.find((page) => page.path === path))
        .filter(Boolean) as WarehouseNavPage[];
      return {
        key: sectionKey,
        label,
        pages: orderedAdminPages,
        primaryPages: orderedAdminPages,
      };
    }

    return {
      key: sectionKey,
      label,
      homePage,
      pages: sectionPages,
      primaryPages: primary,
      advancedPages: advanced.length > 0 ? advanced : undefined,
    };
  });

  let visibleSections = sections.filter((section) => section.pages.length > 0);

  if (allowedSections) {
    visibleSections = visibleSections.filter((section) => allowedSections.has(section.key));
  }

  if (sectionOrder) {
    const rank = new Map(sectionOrder.map((key, index) => [key, index]));
    visibleSections = [...visibleSections].sort((a, b) => {
      const aRank = rank.get(a.key);
      const bRank = rank.get(b.key);
      if (aRank === undefined && bRank === undefined) return 0;
      if (aRank === undefined) return 1;
      if (bRank === undefined) return -1;
      return aRank - bRank;
    });
  }

  return visibleSections;
}

export function getWarehouseLandingPath(user: UserLike, navContext?: NavContext): string {
  const profile = getWarehouseCapabilityProfile(user);
  if (isExternalSafetyReviewer(user) && !profile.canReadIncidents) {
    return '/review';
  }
  const explicitPosture = String((user as { access_posture?: string | null })?.access_posture || '').trim();
  if (explicitPosture === 'product_evaluator' || explicitPosture === 'sandbox_ehs_evaluator') {
    return '/live-view';
  }
  if (isProductEvaluatorCapabilities(getCapabilitiesFromUser(user))) {
    return '/live-view';
  }
  if (isSandboxEhsEvaluatorCapabilities(getCapabilitiesFromUser(user))) {
    return '/live-view';
  }
  const declaredProfiles = getDeclaredWarehouseProfiles(user);
  const sections = resolveWarehouseNavigation(user);
  const visiblePaths = new Set(sections.flatMap((section) => section.pages.map((page) => page.path)));
  const hasDeviceContext = Boolean(String(navContext?.device_id || '').trim());
  const defaultHome = String((user as any)?.default_home || '').trim();

  if (defaultHome && visiblePaths.has(defaultHome)) return defaultHome;

  const profileDeclaredLanding = getDeclaredProfileLandingPath(
    declaredProfiles.primary,
    declaredProfiles.secondary,
    visiblePaths,
  );
  if (profileDeclaredLanding) return profileDeclaredLanding;

  const canSeeUsers = visiblePaths.has('/users');
  const canSeeAccessScope = visiblePaths.has('/admin/access-scope');

  if (canSeeUsers && !canSeeAccessScope) return '/users';
  if (canSeeAccessScope && !canSeeUsers) return '/admin/access-scope';
  if (profile.hasAdminAccess && canSeeUsers) return '/users';

  if (profile.hasSetupWriteAccess) {
    if (visiblePaths.has('/setup-rollout')) return '/setup-rollout';
    if (!hasDeviceContext && visiblePaths.has('/preflight')) return '/preflight';
    if (visiblePaths.has('/devices')) return '/devices';
  }

  if (profile.canReadIncidents && visiblePaths.has('/incidents')) return '/incidents';

  if (visiblePaths.has('/acceptance/overview')) {
    return '/acceptance/overview';
  }

  if (profile.hasAcceptanceAccess && visiblePaths.has('/acceptance/reports')) return '/acceptance/reports';
  if (profile.hasAcceptanceAccess && visiblePaths.has('/acceptance/impact')) return '/acceptance/impact';
  if (profile.hasAcceptanceAccess && visiblePaths.has('/acceptance/proof')) return '/acceptance/proof';

  if (hasDeviceContext && visiblePaths.has('/setup-rollout')) return '/setup-rollout';

  const firstVisiblePage = sections.flatMap((section) => section.pages)[0];
  return firstVisiblePage?.path || '/acceptance/overview';
}

/**
 * Landing path for `/` and `/ocr/*`: capability heuristics plus, when persona preview is active,
 * the preset's `defaultLandingPath` if that route is visible for `user` (typically `effectiveUser`).
 */
export function resolveWarehouseLandingPath(
  user: UserLike,
  navContext?: NavContext,
  previewPersonaId?: string | null,
): string {
  const pid = String(previewPersonaId || '').trim() || null;
  if (pid) {
    const preset = getPersonaPresetById(pid);
    const preferred = String(preset?.defaultLandingPath || '').trim();
    if (preferred) {
      const sections = resolveWarehouseNavigation(user);
      const visiblePaths = new Set(sections.flatMap((section) => section.pages.map((page) => page.path)));
      if (visiblePaths.has(preferred)) {
        return preferred;
      }
    }
  }
  return getWarehouseLandingPath(user, navContext);
}
